import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function ProductCard({ product, onAddToCart, compact = false }) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const pricesHidden = product.prices_hidden || !isAuthenticated;

  const getMinPrice = () => {
    if (!product.tier_pricing || product.tier_pricing.length === 0) return 0;
    return Math.min(...product.tier_pricing.map(t => t.price));
  };

  const getMaxPrice = () => {
    if (!product.tier_pricing || product.tier_pricing.length === 0) return 0;
    return Math.max(...product.tier_pricing.map(t => t.price));
  };

  const stockStatus = () => {
    if (product.stock <= 0) return { label: 'Out of Stock', color: 'text-red-600 bg-red-50' };
    if (product.stock <= (product.low_stock_threshold || 10)) return { label: 'Low Stock', color: 'text-amber-600 bg-amber-50' };
    return { label: 'In Stock', color: 'text-green-700 bg-green-50' };
  };

  const status = stockStatus();
  const image = product.images?.[0] || 'https://images.unsplash.com/photo-1490750967868-88aa4f44baee?w=400';

  return (
    <div data-testid="product-card" className="product-card group bg-white border border-[#e7e3dd] rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200">
      <Link to={`/product/${product.slug}`} className="block relative">
        <div className="aspect-square overflow-hidden bg-[#fbfaf8]">
          <img
            src={image}
            alt={product.name}
            className="product-image w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {product.is_weekly_special && (
            <span className="bg-[#2f6b4f] text-white text-[10px] tracking-wider uppercase px-2 py-0.5 rounded">Weekly Special</span>
          )}
          {product.is_prebook_eligible && (
            <span className="bg-[#b28a4a] text-white text-[10px] tracking-wider uppercase px-2 py-0.5 rounded">Prebook</span>
          )}
        </div>
        {/* Stock badge */}
        <div className="absolute top-2 right-2">
          <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${status.color}`}>{status.label}</span>
        </div>
      </Link>

      <div className="p-3 md:p-4">
        <Link to={`/product/${product.slug}`}>
          <p className="text-[10px] text-[#6f6a63] uppercase tracking-wider mb-1">{product.category}</p>
          <h3 className="text-sm font-semibold text-[#121212] mb-1 line-clamp-2 hover:text-[#2f6b4f] transition-colors">{product.name}</h3>
        </Link>
        
        {product.attributes?.color && (
          <p className="text-[10px] text-[#6f6a63] mb-1">{product.attributes.color}</p>
        )}
        {product.pack_size && (
          <p className="text-[10px] text-[#6f6a63] mb-2">{product.pack_size}</p>
        )}

        <div className="flex items-end justify-between">
          {pricesHidden ? (
            <Link to="/login" className="flex items-center gap-1.5 text-[11px] text-[#2f6b4f] font-medium hover:underline">
              <Lock size={12} />
              Log in to see prices
            </Link>
          ) : (
            <>
              <div>
                <span className="text-xs text-[#6f6a63]">from </span>
                <span className="text-base font-semibold text-[#121212]">${getMinPrice().toFixed(2)}</span>
                {getMaxPrice() > getMinPrice() && (
                  <span className="text-xs text-[#6f6a63]"> - ${getMaxPrice().toFixed(2)}</span>
                )}
              </div>
              {product.stock > 0 && onAddToCart && (
                <button
                  data-testid="product-quick-add"
                  onClick={(e) => { e.preventDefault(); onAddToCart(product._id); }}
                  className="p-2 bg-[#2d2d2d] text-white rounded-lg hover:bg-[#232323] transition-colors active:scale-95"
                  title="Add to Cart"
                >
                  <ShoppingCart size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
